import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AuthenticatedHeader from '../../components/ui/AuthenticatedHeader';
import NavigationBreadcrumbs from '../../components/ui/NavigationBreadcrumbs';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import StatCard from './components/StatCard';
import LeadFilters from './components/LeadFilters';
import LeadTableRow from './components/LeadTableRow';
import LeadTableMobile from './components/LeadTableMobile';
import { leadService } from '../../services/leadService';
import { useAuth } from '../../contexts/AuthContext';

const PartnerDashboard = () => {
  const navigate = useNavigate();
  const { loading: authLoading, isAuthenticated } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateRange, setDateRange] = useState('all');
  const [sortConfig, setSortConfig] = useState({ key: 'submissionDate', direction: 'desc' });

  const [stats, setStats] = useState({
    totalLeads: 0,
    pendingLeads: 0,
    qualifiedLeads: 0,
    closedLeads: 0,
    monthlySubmissions: 0,
    potentialRewards: 0,
  });

  const [leads, setLeads] = useState([]);

  useEffect(() => {
    // Wait for auth state to be ready before loading dashboard data
    if (!authLoading && isAuthenticated) {
      loadDashboardData();
    } else if (!authLoading && !isAuthenticated) {
      // Not authenticated - redirect to login
      setError('Please log in to view your dashboard');
      setTimeout(() => navigate('/partner-authentication'), 2000);
    }
  }, [authLoading, isAuthenticated, navigate]);

  const loadDashboardData = async () => {
    try {
      setIsLoading(true);
      setError('');

      // Load leads and stats in parallel
      const [leadsData, statsData] = await Promise.all([
        leadService?.getAllLeads(),
        leadService?.getLeadStats()
      ]);

      // Convert leads to dashboard format
      const formattedLeads = leadsData?.map(lead => ({
        id: lead?.id,
        customerName: lead?.customerName,
        email: lead?.email,
        propertyType: lead?.propertyType,
        referralType: lead?.referralType,
        submissionDate: lead?.createdAt,
        status: lead?.status,
      }));

      setLeads(formattedLeads);
      setStats(statsData);
    } catch (err) {
      console.error('Error loading dashboard:', err);
      setError(err?.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev?.key === key && prev?.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleResetFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setDateRange('all');
  };

  const filteredLeads = leads?.filter(lead => {
    const matchesSearch = 
      lead?.customerName?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      lead?.email?.toLowerCase()?.includes(searchTerm?.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || lead?.status?.toLowerCase()?.replace(' ', '-') === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  const sortedLeads = [...filteredLeads]?.sort((a, b) => {
    if (sortConfig?.key === 'submissionDate') {
      const dateA = new Date(a.submissionDate);
      const dateB = new Date(b.submissionDate);
      return sortConfig?.direction === 'asc' ? dateA - dateB : dateB - dateA;
    }
    
    const aValue = a?.[sortConfig?.key]?.toString()?.toLowerCase() || '';
    const bValue = b?.[sortConfig?.key]?.toString()?.toLowerCase() || '';
    
    if (sortConfig?.direction === 'asc') {
      return aValue?.localeCompare(bValue);
    }
    return bValue?.localeCompare(aValue);
  });

  if (authLoading || isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <AuthenticatedHeader />
        <NavigationBreadcrumbs />
        <main className="main-content pt-20 px-6 pb-8">
          <div className="max-w-7xl mx-auto">
            <div className="animate-pulse space-y-6">
              <div className="h-8 bg-muted rounded w-1/3"></div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {[1, 2, 3, 4]?.map(i => (
                  <div key={i} className="h-32 bg-muted rounded-lg"></div>
                ))}
              </div>
              <div className="h-64 bg-muted rounded-lg"></div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <AuthenticatedHeader />
        <NavigationBreadcrumbs />
        <main className="main-content pt-20 px-6 pb-8">
          <div className="max-w-7xl mx-auto">
            <div className="bg-destructive/10 border border-destructive rounded-lg p-6">
              <div className="flex items-start gap-4">
                <Icon name="AlertCircle" size={24} color="var(--color-destructive)" />
                <div>
                  <h3 className="text-lg font-semibold text-foreground mb-2">Error Loading Dashboard</h3>
                  <p className="text-muted-foreground mb-4">{error}</p>
                  <Button
                    variant="outline"
                    iconName="RefreshCw"
                    iconPosition="left"
                    onClick={loadDashboardData}
                  >
                    Retry
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <AuthenticatedHeader />
      <NavigationBreadcrumbs />
      <main className="main-content pt-20 px-6 pb-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
            <div>
              <h1 className="text-3xl font-semibold text-foreground mb-2">Partner Dashboard</h1>
              <p className="text-muted-foreground">Track your referrals and manage your referral activities</p>
            </div>
            <Button
              variant="default"
              iconName="Plus"
              iconPosition="left"
              onClick={() => navigate('/create-lead')}
            >
              Create New Referral
            </Button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <StatCard
              label="Total Referrals"
              value={stats?.totalLeads}
              icon="FileText"
              color="primary"
              trend="up"
              trendValue="+12%"
            />
            <StatCard
              label="Pending Review"
              value={stats?.pendingLeads}
              icon="Clock"
              color="warning"
              trend={undefined}
              trendValue={undefined}
            />
            <StatCard
              label="Qualified Referrals"
              value={stats?.qualifiedLeads}
              icon="CheckCircle"
              color="success"
              trend="up"
              trendValue="+8%"
            />
            <StatCard
              label="Closed Deals"
              value={stats?.closedLeads}
              icon="TrendingUp"
              color="secondary"
              trend="up"
              trendValue="+15%"
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
            <div className="bg-card rounded-lg p-6 border border-border elevation-1">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-foreground">Monthly Submissions</h3>
                <Icon name="Calendar" size={20} color="var(--color-primary)" />
              </div>
              <p className="text-3xl font-semibold text-foreground mb-2">{stats?.monthlySubmissions}</p>
              <p className="text-sm text-muted-foreground">Referrals submitted this month</p>
            </div>
            
            <div className="bg-card rounded-lg p-6 border border-border elevation-1">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-foreground">Potential Rewards</h3>
                <Icon name="DollarSign" size={20} color="var(--color-success)" />
              </div>
              <p className="text-3xl font-semibold text-foreground mb-2">${stats?.potentialRewards?.toLocaleString()}</p>
              <p className="text-sm text-muted-foreground">Based on all submitted referrals</p>
            </div>
            
            <div className="bg-card rounded-lg p-6 border border-border elevation-1">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-foreground">Sync Status</h3>
                <Icon name="RefreshCw" size={20} color="var(--color-secondary)" />
              </div>
              <div className="flex items-center gap-2 mb-2">
                <div className="w-2 h-2 rounded-full bg-success animate-pulse"></div>
                <p className="text-lg font-semibold text-foreground">Active</p>
              </div>
              <p className="text-sm text-muted-foreground">Last synced: 2 minutes ago</p>
            </div>
          </div>

          <LeadFilters
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            statusFilter={statusFilter}
            setStatusFilter={setStatusFilter}
            dateRange={dateRange}
            setDateRange={setDateRange}
            onReset={handleResetFilters}
          />

          <div className="bg-card rounded-lg border border-border elevation-1 overflow-hidden">
            <div className="p-6 border-b border-border bg-muted/30 flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold text-foreground flex items-center gap-2">
                  <Icon name="List" size={20} />
                  All Referrals
                </h2>
                <p className="text-sm text-muted-foreground mt-1">
                  Showing {sortedLeads?.length} of {leads?.length} referrals
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                iconName="Download"
                iconPosition="left"
              >
                Export
              </Button>
            </div>

            <div className="hidden lg:block overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/30 border-b border-border">
                  <tr>
                    <th 
                      className="px-6 py-4 text-left text-sm font-semibold text-foreground cursor-pointer hover:bg-muted/50 transition-colors duration-200"
                      onClick={() => handleSort('customerName')}
                    >
                      <div className="flex items-center gap-2">
                        Customer
                        <Icon 
                          name={sortConfig?.key === 'customerName' && sortConfig?.direction === 'asc' ? 'ChevronUp' : 'ChevronDown'} 
                          size={16} 
                        />
                      </div>
                    </th>
                    <th 
                      className="px-6 py-4 text-left text-sm font-semibold text-foreground cursor-pointer hover:bg-muted/50 transition-colors duration-200"
                      onClick={() => handleSort('propertyType')}
                    >
                      <div className="flex items-center gap-2">
                        Property Type
                        <Icon 
                          name={sortConfig?.key === 'propertyType' && sortConfig?.direction === 'asc' ? 'ChevronUp' : 'ChevronDown'} 
                          size={16} 
                        />
                      </div>
                    </th>
                    <th 
                      className="px-6 py-4 text-left text-sm font-semibold text-foreground cursor-pointer hover:bg-muted/50 transition-colors duration-200"
                      onClick={() => handleSort('referralType')}
                    >
                      <div className="flex items-center gap-2">
                        Referral Type
                        <Icon 
                          name={sortConfig?.key === 'referralType' && sortConfig?.direction === 'asc' ? 'ChevronUp' : 'ChevronDown'} 
                          size={16} 
                        />
                      </div>
                    </th>
                    <th 
                      className="px-6 py-4 text-left text-sm font-semibold text-foreground cursor-pointer hover:bg-muted/50 transition-colors duration-200"
                      onClick={() => handleSort('submissionDate')}
                    >
                      <div className="flex items-center gap-2">
                        Submitted
                        <Icon 
                          name={sortConfig?.key === 'submissionDate' && sortConfig?.direction === 'asc' ? 'ChevronUp' : 'ChevronDown'} 
                          size={16} 
                        />
                      </div>
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-foreground">Status</th>
                    <th className="px-6 py-4 text-right text-sm font-semibold text-foreground">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {sortedLeads?.map((lead) => (
                    <LeadTableRow key={lead?.id} lead={lead} />
                  ))}
                </tbody>
              </table>
            </div>

            <div className="lg:hidden p-4">
              <LeadTableMobile leads={sortedLeads} />
            </div>
          </div>

          {sortedLeads?.length === 0 && (
            <div className="bg-card rounded-lg border border-border elevation-1 p-12 text-center mt-6">
              <Icon name="Search" size={48} className="mx-auto mb-4 text-muted-foreground" />
              <h3 className="text-lg font-semibold text-foreground mb-2">No referrals found</h3>
              <p className="text-muted-foreground mb-6">Try adjusting your search or filter criteria</p>
              <Button
                variant="outline"
                iconName="X"
                iconPosition="left"
                onClick={handleResetFilters}
              >
                Clear Filters
              </Button>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default PartnerDashboard;